import {
  adjust,
  always,
  both,
  compose,
  cond,
  equals,
  join,
  map,
  pipe,
  replace,
  split,
  toLower,
  toUpper,
  keys,
  type,
  T,
} from 'ramda';
import dayjs from 'dayjs';
import { MonthAndYear } from 'shared/types/file-report-history.types';

/*
 * Create a new object composed of properties picked from another object by
 * omitting the given properties. The inverse of {@link pick}.
 *
 * @param  {object} obj The object to pick properties from
 * @param  {string[]} props An array of properties to omit
 * @return {Object} The new object
 * @deprecated use import { omit } from 'ramda'
 */
export function omit<
  T extends Record<string, unknown>,
  K extends keyof T
>
(obj: T, props: Array<K>): Record<string, unknown> {
  if (props.length === 0) {
    return { ...obj };
  }

  const picked = {} as Record<string, unknown>;

  Object.keys(obj).forEach((key) => {
    if (!props.includes(key as K)) {
      picked[key] = obj[key];
    }
  });

  // Return new   bject
  return picked;
}

/**
 * Parse a query string into a Map of key to value
 *
 * @param search the query string to parse (e.g. "?key1=val1&key2=val2")
 * @returns a Map of keys to values
 */
export function parseQueryString(search: string): Map<string, string> {
  if (search.length < 4) {
    return new Map();
  }

  let s = search;

  if (s.charAt(0) === '?') {
    s = s.slice(1);
  }

  return s
    .split('&')
    .reduce((mp, token) => {
      const [ key, val ] = token.split('=').slice(0,2);
      mp.set(key, val);

      return mp;
    }, new Map());
}

/**
 * Checks if an object has any owned properties. E.g. is it equivalent to {}
 *
 * @param {object} obj the object to check
 * @returns {boolean}
 */
export function objectIsEmpty(obj: object): boolean {
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      return false;
    }
  }

  return true;
}

/**
 * Debounce functions for better performance
 * (c) 2018 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param  {Function} fn The function to debounce
 * @param  {number} timeSpan Time span for which a function needs to be debounced
 */
export function debounce(fn: Function, timeSpan: number) {
  // Setup a timer
  let timeout: number;

  // Return a function to run debounced
  return function (...args: any[]) {
    if (timeout) {
      window.clearTimeout(timeout);
    }
    // Setup the new requestAnimationFrame()
    timeout = window.setTimeout(() => {
      fn.apply(null, args);
    }, timeSpan);
  };
}

/**
 * Convert any string from any case to uppercase the first letter and the rest lower case.
 * @param {string}
 * @returns {string}
 */
export const upperFirst = compose(join(''), adjust(0, toUpper), split(''));

/**
 * Convert any string from any case to name case, e.f. First Last
 * @param {string}
 * @returns {string}
 */
export const nameCase = compose(join(' '), map(upperFirst), split(' '), toLower);

export const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

/**
 * Returns an empty function
 * @returns {Function}
 */
export const noop = always(undefined);

/**
 * Applies a mask to a number or string. Used for sensitive information such as SSN
 */
export function mask(value: string | number, len = 5, char = 'X') {
  if (char.length > 1) {
    throw new Error('The char argument must be exactly one character');
  }

  const stringValue = value.toString();

  return char.repeat(Math.min(len, stringValue.length)) + stringValue.substring(Math.min(len, stringValue.length));
}

/**
 * Converts a 'Page Title' to a url usable string
 *
 * @param {string} title
 * @returns {string}
 */
export const slugFromTitle = pipe(toLower, replace(/\s+/g, '-'));

/**
 * Converts an url slug ("page-title") to a title ("Page Title")
 *
 * @param {string} title
 * @returns {string}
 */
export const titleFromSlug = pipe(replace(/-+/g, ' '), nameCase);

/**
 * Determine if date is between two YYYY-MM-DD dates
 */
export function isWithinDateRange(
  yearBeginning: string,
  yearEnd: string,
  date?: Date,
) {
  return both(
    always(dayjs(date)
      .add(1, 'day')
      .isAfter(dayjs(yearBeginning, 'YYYY-MM-DD'))),

    always(dayjs(date)
      .subtract(1, 'day')
      .isBefore(dayjs(yearEnd, 'YYYY-MM-DD'))),
  );
}

export function setLegacySiteParams(query: string) {
  localStorage.setItem('legacyQueryParams', query.replace('&', '?'));
}

export function getLegacySiteParams() {
  return localStorage.getItem('legacyQueryParams') ?? '';
}

/**
 * @deprecated
 *
 * Utility for navigating user to legacy enrollment site. To be removed once we
 * port enrollment to Dash
 */
export function createAndSubmitForm(form: HTMLFormElement, fieldValue: string) {
  form.method = 'post';
  form.action = 'https://www.flores247.com/reenrollment/reenroll.cshtml';
  form.target = '_blank';
  const hiddenField = document.createElement('input');
  hiddenField.type = 'hidden';
  hiddenField.name = 'planid';
  hiddenField.value = fieldValue;
  form.appendChild(hiddenField);
  document.body.appendChild(form);
  form.submit();

  // Cannot immediately resolve or post will not redirect
  return new Promise((r) => setTimeout(r, 500));
}

/**
 * Find the first path to a "leaf" in a nested Object.
 * A leaf is defined as a property with a primitive value.
 *
 * For example, leafPath({a: 1}) returns 'a'. leafPath({a: {b: {c: 1}}}) returns 'a.b.c'
 * Works with arrays, too: leafPath({a: {b: [{c: 1, d: 'f'}], x: 2}}) returns 'a.b.0.c'
 *
 * Unfortunately, the same value cannot be guaranteed every time across JS interpreters
 * because `keys` doens't carry that guarantee. In practice, this is probably fine.
 *
 * @param obj {Object} the object to f
 * @returns a string representing the path to the first leaf
 */
export function leafPath(obj: Object): string | undefined {
  const key = keys(obj).shift();

  if (key) {
    const valueType = type(obj[key]).toLowerCase();

    if ([ 'object', 'array' ].includes(valueType)) {
      return `${key}.${leafPath(obj[key])}`;
    }
  }

  // got to the end
  return key?.toString();
}

export function planCodeToPlanType(planCode: string, subName = '') {
  return cond([
    [ equals('M'), always('MRA') ],
    [ equals('D'), always('DCRA') ],
    [ equals('T'), always('QTE') ],
    [ equals('A'), always('Retiree') ],
    [ equals('H'), always('HRA') ],
    [ equals('P'), always('Premium') ],
    [ equals('S'), always('HSA') ],
    [ equals('-'), always('LFSA') ],
    [ equals('0'), always(subName) ],
    [ T, always(undefined) ],
  ])(planCode as never);
}

export function getYearAndMonth(monthYear: string) : MonthAndYear {
  const parsed = dayjs(monthYear, 'YYYY-M');
  const year = parsed.year();
  const month = parsed.month() + 1;

  return { year, month };
}

export function toNumber(val: any) {
  return val !== '0' ? parseInt(val) : 0;
}

export function valueOrPlaceholder(value: string | number | undefined | null, placeholder: string) {
  return !value || value === '' ? placeholder : value;
}

export function dateOrPlaceholder(date: string | undefined, format: string, placeholder: string) {
  return !date || date === '' ? placeholder : dayjs(date).format(format);
}
